import StopIcon from "@assets/icons/StopIcon";
import Copied from "@assets/icons/copied.svg";
import Copy from "@assets/icons/copy.svg";
import { CommonInput } from "@commonComponents/input";
import FirstRationalStep from "@components/pages/project/single-functions/climate-rationale-advisor/form/first-step";
import { validateMessages } from "@utils/json-data";
import { Form, Upload } from "antd";
import classNames from "classnames";
import React, { FC, memo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "@stores/root-store";
import style from "./style.module.scss";
import SpinnerGeneration from "../../../common-components/spinner-generating";
import ReportModal from "@components/modal/report-modal";
import removeMd from "remove-markdown";
import Close from "@assets/icons/cross.svg";
import { resizeImageAsync } from "@utils/common-functions";
import UploadIcon from "@assets/icons/upload.svg";

interface RationalFormProps {
  Text: JSX.Element;
  disabled: boolean;
  maxLength: number;
  handleSubmit: (saveOnly: boolean) => void;
  form: any;
  firstStepForm: any;
  initialState: any;
  placeholder?: string;
  isFormPage?: boolean;
  isSubmitting?: boolean;
  value?: string;
  isItalicFontStyle?: boolean;
  section?: string;
  isValid?: boolean;
  setIsValid?: (data: boolean) => void;
  firstStepInitialAnswers?: any;
  projectName?: string;
  handleStopGenerate: () => void;
  handleViewReport: () => void;
  sectionOutputPresent: boolean;
  isLoading: boolean;
  isAnswer?: boolean;
  base64Images?: string[] | null;
  setBase64Images?: React.Dispatch<React.SetStateAction<string[] | null>>;
  allowUpload: boolean | undefined;
  selectedFiles: any[] | null;
  setSelectedFiles: React.Dispatch<React.SetStateAction<any[] | null>>;
}

const RationalForm: FC<RationalFormProps> = ({
  Text,
  disabled,
  maxLength,
  handleSubmit,
  form,
  firstStepForm,
  initialState,
  placeholder,
  isFormPage,
  value,
  isSubmitting,
  isItalicFontStyle,
  section,
  isValid,
  setIsValid,
  firstStepInitialAnswers,
  projectName,
  handleStopGenerate,
  handleViewReport,
  sectionOutputPresent,
  isLoading,
  isAnswer,
  base64Images,
  setBase64Images,
  allowUpload,
  selectedFiles,
  setSelectedFiles,
}) => {
  const {
    user: { handleCheckInputTokensValid },
  } = useStore(null);
  const [copied, setCopied] = useState(false);

  const { pathname } = useLocation();

  const type = pathname.split("/")[3];
  const step = pathname.split("/")[4];

  const isFirstStep = +step === 1;

  let q_id: string = "";

  if (isFormPage) {
    const keys =
      isFirstStep && type === "results"
        ? Object.keys(firstStepInitialAnswers)
        : Object.keys(initialState ? initialState : {});
    const keysWithoutImages = keys.filter((key) => key !== "images");
    q_id = keysWithoutImages[0]; // This is the single key
  } else {
    q_id = `q${+step + 1}`;
  }

  const checkLengthOfInput = async (value: string | object) => {
    const data = {
      functionality: "climate rationale",
      section: section,
      project_name: projectName,
      questions:
        +step === 1
          ? value
          : {
              [q_id]: value,
            },
    };

    await handleCheckInputTokensValid(data).then((res) => {
      try {
        const result = JSON.parse(res.isValid);
        setIsValid(result);
      } catch (error) {
        setIsValid(false);
      }
    });
  };

  useEffect(() => {
    if (!copied) return;

    setTimeout(() => {
      setCopied(null);
    }, 2000);
  }, [copied]);

  useEffect(() => {
    async function resizeAndSetImage() {
      if (selectedFiles?.length > 0) {
        try {
          const base64Images = [];

          for (const e of selectedFiles) {
            const img = await resizeImageAsync(e.originFileObj, 1024, 768);
            base64Images.push(img);
          }

          setBase64Images(base64Images); // Store the base64 image in state
        } catch (error) {
          console.error("Error resizing image:", error);
        }
      } else {
        setBase64Images([]);
      }
    }

    resizeAndSetImage();
  }, [selectedFiles]); // The effect depends on `selectedFile`

  return type === "form" && step === "1" ? (
    <FirstRationalStep
      isLoading={isLoading}
      firstStepForm={firstStepForm}
      firstStepInitialAnswers={firstStepInitialAnswers}
      checkLengthOfInput={checkLengthOfInput}
      sectionOutputPresent={sectionOutputPresent}
      handleViewReport={handleViewReport}
      handleSubmit={handleSubmit}
      isFormPage={isFormPage}
      selectedFiles={selectedFiles}
      setSelectedFiles={setSelectedFiles}
      base64Images={base64Images}
      setBase64Images={setBase64Images}
      allowUpload={allowUpload}
    />
  ) : (
    <>
      <div className={style.formWrapper}>
        <Form
          className={style.form}
          form={form}
          autoComplete="false"
          onFinish={handleSubmit}
          validateMessages={validateMessages}
          layout="vertical"
          initialValues={{ q_id }}
        >
          <div className={style.labelContainer}>
            <span className={style.label}>{Text}</span>
            {type === "results" ? null : (
              <span
                className={
                  isLoading === false && sectionOutputPresent
                    ? style.viewOutputButton
                    : style.viewOutputButtonDisabled
                }
                onClick={() =>
                  sectionOutputPresent ? handleViewReport() : null
                }
              >
                View Last Output
              </span>
            )}
          </div>
          <div className={style.textareaWrapper}>
            <Form.Item>
              {allowUpload && isFormPage && base64Images && (
                <div className={style.previewWrapper}>
                  {base64Images.map((img, index) => (
                    <div key={index}>
                      <img src={img} alt="preview" />
                      <div
                        className={style.clearFile}
                        onClick={() => {
                          setBase64Images((prevImages: string[]) =>
                            prevImages.filter((_, i) => i !== index),
                          );
                          setSelectedFiles((prevFiles) =>
                            prevFiles.filter((_, i) => i !== index),
                          );
                        }}
                      >
                        <img src={Close} alt="Clear file" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Form.Item
                name={q_id}
                rules={[
                  { required: !disabled, message: "This field is required" },
                ]}
              >
                {!isAnswer && isSubmitting ? (
                  <SpinnerGeneration />
                ) : (
                  <CommonInput
                    placeholder={
                      isSubmitting
                        ? "If you don’t see any output below within 7 seconds, you may have exceeded the permitted word limit in your input. Please reduce your input - thank you!"
                        : placeholder || ""
                    }
                    value={
                      isFormPage && initialState && !isSubmitting
                        ? initialState[q_id]
                        : ""
                    }
                    defaultValue={!isFormPage && !isSubmitting ? value : ""}
                    disabled={disabled}
                    size={3}
                    // TODO: removed input limit until further notice
                    // maxlength={maxLength * 10}
                    // onChange={(e) => {
                    //   checkLengthOfInput(e.target.value);
                    // }}
                    inputType="textarea"
                    style={{
                      cursor: isLoading ? "wait" : "",
                      minHeight: "55vh",
                    }}
                    autoSizeCheck={{
                      minRows: isFormPage ? 19 : 2,
                      maxRows: 1111,
                    }}
                    className={classNames(style.textarea)}
                    isMarkdown={!isFormPage}
                  />
                )}
              </Form.Item>
            </Form.Item>
            {!isFormPage &&
              !isSubmitting &&
              (value || form.getFieldValue(q_id)) &&
              (copied ? (
                <img src={Copied} alt="copy" />
              ) : (
                <img
                  src={Copy}
                  alt="copy"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      removeMd(form.getFieldValue(q_id)),
                    );
                    setCopied(true);
                  }}
                />
              ))}
          </div>
        </Form>
        {allowUpload && isFormPage && (
          <div className={style.uploadButtonBlock}>
            <div className={style.uploadButtonWrapper}>
              <Upload
                accept="image/*"
                onChange={(info) => {
                  setSelectedFiles(info.fileList);
                }}
                multiple
                fileList={[]}
                itemRender={() => null}
              >
                <div className={style.uploadButtonBlockButton}>
                  <img alt="upload" src={UploadIcon} />
                  <span>Upload Images</span>
                </div>
              </Upload>
            </div>
          </div>
        )}
        {!isValid && (
          <div style={{ color: "red", fontSize: "14px" }}>
            You have exceeded the maximum length for this field
          </div>
        )}
        {/*<div style={{ paddingTop: "5px" }}>*/}
        {/*  The max word limit for this page is 1000 words*/}
        {/*</div>*/}
        {!isFormPage && (
          <div
            style={{
              border: "1px solid #E0E0E0",
              position: "relative",
              top: "-16px",
              margin: "1px",
              padding: "10px 17px",
              display: "flex",
              justifyContent: "end",
              borderRadius: "0 0 5px 5px",
            }}
          >
            <div
              style={{
                height: "fit-content",
                cursor: "pointer",
              }}
              onClick={() => {
                if (isSubmitting) {
                  handleStopGenerate();
                }
              }}
            >
              <StopIcon color={!isSubmitting ? "#dddddd" : ""} />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(RationalForm);
